import React, { useEffect, useState } from "react";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemText from "@mui/material/ListItemText";
import EditIcon from "@mui/icons-material/Edit";
import VisibilityIcon from "@mui/icons-material/Visibility";
import IconButton from "@mui/material/IconButton";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import useActions, { useAppSelector } from "../../../app/hooks";

import { selectLibrary } from "../../library/librarySlice";
import { Factotum } from "../../../types/Factotum";
import FactotumStateModal from "./FactotumStateModal";

export default function FactotumsList(props: {
  parentCloseHandle: () => void;
}) {
  const actions = useActions();

  const [createMode, setCreateMode] = React.useState<boolean>(false);
  const [editMode, setEditMode] = React.useState<boolean>(false);
  const [selectedFactotum, setSelectedFactotum] = React.useState<Factotum | null>(null);
  const [stateModalFactotum, setStateModalFactotum] = React.useState<Factotum | null>(null);

  const [name, setName] = useState("");
  const [nameError, setNameError] = React.useState("");

  const librarySlice = useAppSelector(selectLibrary);

  useEffect(() => {
    actions.getFactotums();
  }, []);

  function handleNameChange(e: React.ChangeEvent<HTMLInputElement>) {
    if (e.target.value.trim() === "") {
      setNameError("Invalid name");
    } else setNameError("");
    setName(e.target.value);
  }

  function resetFormFields() {
    setName("");
    setNameError("");
    setCreateMode(false);
    setEditMode(false);
    setSelectedFactotum(null);
  }

  function handleCreateFactotum() {
    actions.createFactotum({ name: name.trim() });
    resetFormFields();
  }

  function handleEditFactotum(factotum: Factotum) {
    setSelectedFactotum(factotum);
    setEditMode(true);
    setName(factotum.name || "");
  }

  function handleUpdateFactotum() {
    if (selectedFactotum) {
      actions.updateFactotum({
        factotumId: selectedFactotum.id,
        factotumUpdateRequest: { name: name.trim() },
      });
      resetFormFields();
    }
  }

  return (
    <Dialog open={true} onClose={() => props.parentCloseHandle()} maxWidth="sm" fullWidth>
      <DialogTitle>Factotums</DialogTitle>

      <DialogContent>
        {!createMode && !editMode && (
          <Box sx={{ pt: 2 }}>
            {librarySlice.factotums.length === 0 && (
              <Typography sx={{ mb: 2, textAlign: "center" }}>
                No factotums in the workspace...
              </Typography>
            )}

            <List>
              {librarySlice.factotums.map((factotum) => (
                <ListItem
                  key={"factotum_deets_" + factotum.id}
                  sx={{ py: 1.5 }}
                  secondaryAction={
                    <Box sx={{ display: "flex", gap: 0.5 }}>
                      <IconButton
                        edge="end"
                        onClick={() => setStateModalFactotum(factotum)}
                        size="small"
                        title="View state"
                      >
                        <VisibilityIcon />
                      </IconButton>
                      <IconButton
                        edge="end"
                        onClick={() => handleEditFactotum(factotum)}
                        size="small"
                        title="Edit Factotum"
                      >
                        <EditIcon />
                      </IconButton>
                    </Box>
                  }
                >
                  <ListItemText primary={factotum.name} />
                </ListItem>
              ))}
            </List>
          </Box>
        )}
        {createMode && (
          <Box component="form" sx={{ pt: 2 }}>
            <DialogContentText sx={{ mb: 3 }}>Create a new factotum</DialogContentText>
            <TextField
              autoFocus
              margin="dense"
              id="name"
              label="Factotum name"
              error={nameError !== ""}
              helperText={nameError}
              fullWidth
              variant="standard"
              value={name}
              onChange={handleNameChange}
              sx={{ mb: 2 }}
            />
          </Box>
        )}
        {editMode && (
          <Box component="form" sx={{ pt: 2 }}>
            <DialogContentText sx={{ mb: 3 }}>Edit factotum</DialogContentText>
            <TextField
              autoFocus
              margin="dense"
              id="name"
              label="Factotum name"
              error={nameError !== ""}
              helperText={nameError}
              fullWidth
              variant="standard"
              value={name}
              onChange={handleNameChange}
              sx={{ mb: 2 }}
            />
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2, pt: 1 }}>
        <Button
          onClick={() => {
            if (createMode || editMode) {
              resetFormFields();
            } else {
              props.parentCloseHandle();
            }
          }}
        >
          Cancel
        </Button>
        {!createMode && !editMode && (
          <Button
            variant="contained"
            color="primary"
            onClick={() => setCreateMode(true)}
          >
            Create Factotum
          </Button>
        )}
        {createMode && (
          <Button
            variant="contained"
            color="primary"
            onClick={() => handleCreateFactotum()}
          >
            Confirm
          </Button>
        )}
        {editMode && (
          <Button
            variant="contained"
            color="primary"
            onClick={() => handleUpdateFactotum()}
          >
            Update
          </Button>
        )}
      </DialogActions>

      <FactotumStateModal
        open={stateModalFactotum !== null}
        factotum={stateModalFactotum}
        onClose={() => setStateModalFactotum(null)}
      />
    </Dialog>
  );
}
